import React, { useState, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Image, ScrollView, Alert, Platform,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { supabase } from '../lib/supabase';
import { useAppTheme } from '../contexts/ThemeContext';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

export default function PhotoAttachments({ quoteId, photos = [], onPhotosChange, table = 'quotes', bucket = 'quote-photos' }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const [uploading, setUploading] = useState(false);

  async function pickImage() {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Allow photo access to attach images.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.7,
        allowsMultipleSelection: false,
      });
      if (result.canceled || !result.assets?.[0]) return;

      const asset = result.assets[0];

      // Extract extension from URI, stripping query strings, then validate
      const rawExt = (asset.uri.split('.').pop() || 'jpg').toLowerCase().split('?')[0];
      const ext = ALLOWED_EXTENSIONS.includes(rawExt) ? rawExt :
                  (asset.mimeType === 'image/png' ? 'png' :
                   asset.mimeType === 'image/webp' ? 'webp' : 'jpg');

      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        Alert.alert('Invalid file type', 'Please select a JPG, PNG, or WebP image.');
        return;
      }

      setUploading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const fileName = `${user.id}/${quoteId}/${Date.now()}.${ext}`;

      // Use mimeType from the asset (trusted source) rather than deriving from ext
      const contentType = asset.mimeType || 'image/jpeg';

      // Fetch the image as blob
      const response = await fetch(asset.uri);
      const blob = await response.blob();

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blob, { contentType, upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName);

      const updated = [...photos, publicUrl];
      onPhotosChange(updated);

      // Persist to DB
      await supabase.from(table).update({ photos: updated }).eq('id', quoteId);
    } catch (err) {
      Alert.alert('Upload failed', err.message);
    } finally {
      setUploading(false);
    }
  }

  async function removePhoto(url) {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Remove this photo?')
      : await new Promise((resolve) =>
          Alert.alert('Remove Photo', 'Remove this photo from the quote?', [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
          ])
        );
    if (!confirmed) return;
    const updated = photos.filter((p) => p !== url);
    onPhotosChange(updated);
    await supabase.from(table).update({ photos: updated }).eq('id', quoteId);
  }

  return (
    <View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.row}>
        {photos.map((uri, i) => (
          <TouchableOpacity key={i} onLongPress={() => removePhoto(uri)} activeOpacity={0.85}>
            <Image source={{ uri }} style={styles.photo} />
          </TouchableOpacity>
        ))}
        <TouchableOpacity style={styles.addPhoto} onPress={pickImage} activeOpacity={0.7} disabled={uploading}>
          <Text style={styles.addPhotoIcon}>{uploading ? '⏳' : '+'}</Text>
          <Text style={styles.addPhotoText}>{uploading ? 'Uploading...' : 'Add Photo'}</Text>
        </TouchableOpacity>
      </ScrollView>
      {photos.length > 0 && <Text style={styles.hint}>Long-press photo to remove</Text>}
    </View>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    row: { gap: spacing.sm, paddingVertical: spacing.xs },
    photo: { width: 80, height: 80, borderRadius: radius.md, backgroundColor: colors.surfaceElevated },
    addPhoto: {
      width: 80, height: 80, borderRadius: radius.md, borderWidth: 1, borderStyle: 'dashed',
      borderColor: colors.border, alignItems: 'center', justifyContent: 'center',
      backgroundColor: colors.surface,
    },
    addPhotoIcon: { fontSize: 20, color: colors.textMuted, marginBottom: 2 },
    addPhotoText: { fontSize: 9, color: colors.textMuted, textAlign: 'center' },
    hint: { fontSize: typography.sizes.xs, color: colors.textMuted, marginTop: spacing.xs },
  });
}
